import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import { List, DeviceIcon, DescriptionTable, IconDisabled, IconCheckmark } from 'shared/components'
import { sendErrorReport, displayValue, formatDate } from 'shared/helpers'
import { defaultDateFormat } from 'shared/constants'
import { getLicenseUserDevices } from 'src/devices/actions'

export default function DevicesTab() {
  const { userID, companyId } = useParams()
  const history = useHistory()
  const [list, setList] = useState([])
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [tableExpanded, setTableExpanded] = useState({})

  const updateGroups = page => {
    setLoading(true)
    getLicenseUserDevices(companyId, userID, page)
      .then(res => {
        setList(get(res, 'data.results', []))
        setLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot get license user devices')
        setLoading(false)
      })
  }

  useEffect(() => {
    updateGroups(page)
  }, [page])

  const handleUserPageChange = page => {
    setPage(page)
    updateGroups(page)
  }

  const redirectToDevicePage = rowData => {
    const deviceId = get(rowData, 'original.id')
    history.push(`/${companyId}/devices/${deviceId}`)
  }

  return (
    <>
      <List
        clickable
        data={list}
        page={page}
        pages={Math.ceil(get(list, 'count') / 20)}
        loading={loading}
        manual
        minRows={get(list, 'length') || 2}
        showPagination
        onPageChange={handleUserPageChange}
        handleClick={rowData => redirectToDevicePage(rowData)}
        pageSize={20}
        onExpandedChange={expanded => setTableExpanded(expanded)}
        expanded={tableExpanded}
        SubComponent={row => {
          const isBorrowed = get(row, 'original.borrowed_until')
          return (
            <div className='SubComponent'>
              <DescriptionTable
                details={[
                  {
                    label: isBorrowed ? __('Borrowed until') : null,
                    value: formatDate(get(row, 'original.borrowed_until'), defaultDateFormat)
                  },
                  {
                    label: __('First activated'),
                    value: formatDate(get(row, 'original.time_activated'), defaultDateFormat)
                  },
                  {
                    label: __('OS'),
                    value: displayValue(get(row, 'original.os'))
                  },
                  {
                    label: __('SDK build'),
                    value: displayValue(get(row, 'original.sdk_build_version'))
                  },
                  {
                    label: __('App version'),
                    value: displayValue(get(row, 'original.app_ver'))
                  },
                  {
                    label: __('Hostname'),
                    value: displayValue(get(row, 'original.hostname'))
                  },
                  {
                    label: __('External IP address'),
                    value: displayValue(get(row, 'original.external_ip'))
                  },
                  {
                    label: __('IP address'),
                    value: displayValue(get(row, 'original.ip'))
                  },
                  {
                    label: __('MAC address'),
                    value: displayValue(get(row, 'original.mac_address'))
                  },
                  {
                    label: __('Is virtual machine'),
                    value: get(row, 'original.is_vm') ? __('Yes') : __('No')
                  },
                  {
                    label: __('VM info'),
                    value: displayValue(get(row, 'original.vm_info'))
                  }
                ]}
              />
            </div>
          )
        }}
        columns={[
          {
            expander: true,
            Header: __('Details'),
            headerClassName: 'text-center',
            width: 80,
            style: {
              fontSize: 25,
              padding: '0',
              textAlign: 'center',
              userSelect: 'none'
            }
          },
          {
            accessor: 'type',
            Header: __('Type'),
            className: 'text-center',
            headerClassName: 'text-center',
            width: 70,
            Cell: cellInfo => (
              <div className='device-type-icon'>
                <DeviceIcon device={cellInfo.value} />
              </div>
            )
          },
          {
            accessor: 'hardware_id',
            Header: __('Hardware ID'),
            Cell: rowData => {
              return displayValue(rowData.value)
            },
            maxWidth: 200,
            show: true
          },
          {
            accessor: 'last_check',
            Header: __('Last Check'),
            maxWidth: 200,
            Cell: rowData => {
              const isBundle = get(rowData, 'original.is_bundle')
              if (isBundle) return '-'
              const isEnabled = get(rowData, 'original.enabled')
              if (!isEnabled) {
                return '-'
              }
              return formatDate(rowData.value, defaultDateFormat)
            },
            show: true
          },
          {
            accessor: 'device_active',
            className: 'text-center',
            Header: __('Status'),
            headerClassName: 'text-center',
            Cell: rowData => {
              return rowData.value ? (
                <IconCheckmark color='#10ac84' height='14px' />
              ) : (
                <IconDisabled color='#aaa' height='14px' />
              )
            },
            width: 80,
            show: true
          },
          {
            accessor: 'device_variables',
            className: 'text-center',
            Header: __('Device Variables'),
            headerClassName: 'text-center',
            Cell: cellInfo => displayValue(JSON.stringify(cellInfo.value)),
            minWidth: 100,
            show: true
          },
          {
            accessor: 'floating_in_use',
            Header: __('Floating in use'),
            maxWidth: 150,
            Cell: rowData => {
              return rowData.value ? (
                <IconCheckmark color='#10ac84' height='14px' />
              ) : (
                <IconDisabled color='#aaa' height='14px' />
              )
            },
            show: true
          },
          {
            accessor: 'borrowed_until',
            Header: __('Borrowed'),
            Cell: rowData => {
              return rowData.value ? (
                <IconCheckmark color='#10ac84' height='14px' />
              ) : (
                <IconDisabled color='#aaa' height='14px' />
              )
            },
            show: true
          },
          {
            accessor: 'floating_last_seen',
            className: 'text-center',
            Header: __('Floating last check'),
            headerClassName: 'text-center',
            Cell: rowData => {
              return rowData.value ? (
                <IconCheckmark color='#10ac84' height='14px' />
              ) : (
                <IconDisabled color='#aaa' height='14px' />
              )
            },
            minWidth: 100,
            show: true
          }
        ]}
      />
    </>
  )
}
